/**
 * CoachingTabContent Component
 * Content for the Coaching tab in the profile detail view
 * Shows coaching sessions created by the user
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { CoachingSessionCard } from "@/components/cards/CoachingSessionCard"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { SessionBookingsModal } from "@/features/coaching/components/SessionBookingsModal"
import type { BookingTab } from "@/features/coaching/hooks/useCoachingBookings"
import {
  useDeleteCoachingSession,
  useMyCoachingSessions,
} from "@/features/coaching/hooks/useCoachingMutations"
import { useToggleCoachingSave } from "@/features/coaching/hooks/useSavedCoachingSessions"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"

interface CoachingTabContentProps {
  userId: string
  className?: string
  searchQuery?: string
  showHeader?: boolean
  isGuest?: boolean
  /** When true, session cards show "Paused" state (coaching toggle is off) */
  isCoachingPaused?: boolean
  /** When true, fetches sessions even when coaching is paused (my-rituality owner view) */
  includePaused?: boolean
}

export function CoachingTabContent({
  userId: _userId,
  className,
  searchQuery,
  showHeader = true,
  isGuest = false,
  isCoachingPaused = false,
  includePaused = false,
}: CoachingTabContentProps) {
  const { t } = useTranslation("profile")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const [showLoginModal, setShowLoginModal] = useState(false)
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)
  const toggleSave = useToggleCoachingSave()
  const navigate = useNavigate()
  const deleteSession = useDeleteCoachingSession()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState<{ id: string; title: string } | null>(null)

  // Session Bookings Modal state
  const [bookingsModalOpen, setBookingsModalOpen] = useState(false)
  const [selectedSession, setSelectedSession] = useState<{
    id: string
    title: string
    coverImage?: string | null
    participationFee: string
    price: number | null
    duration: number
    format: string
    expert: {
      id: string
      name: string | null
      username: string | null
      profilePictureUrl?: string | null
    }
  } | null>(null)
  const [bookingsInitialTab, setBookingsInitialTab] = useState<BookingTab>("upcoming")

  const {
    data: coachingData,
    isLoading,
    isError,
  } = useMyCoachingSessions(_userId, {
    limit: 50,
    search: debouncedSearch || undefined,
    status: "live",
    includePaused,
  })

  const sessions = coachingData?.sessions ?? []

  const handleDeleteClick = (sessionId: string, sessionTitle: string) => {
    setSessionToDelete({ id: sessionId, title: sessionTitle })
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (sessionToDelete) {
      deleteSession.mutate(sessionToDelete.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setSessionToDelete(null)
        },
      })
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setSessionToDelete(null)
  }

  // Handlers for bookings modal
  const handleBookingsClick = (session: {
    id: string
    title: string
    coverImage?: string | null
    participationFee: string
    price: number | null
    duration: number
    format: string
    creator?: {
      id: string
      name: string | null
      username: string | null
      profilePictureUrl?: string | null
    } | null
  }) => {
    setSelectedSession({
      ...session,
      expert: {
        id: session.creator?.id || "",
        name: session.creator?.name || null,
        username: session.creator?.username || null,
        profilePictureUrl: session.creator?.profilePictureUrl,
      },
    })
    setBookingsInitialTab("upcoming")
    setBookingsModalOpen(true)
  }

  const handleCompletedClick = (session: {
    id: string
    title: string
    coverImage?: string | null
    participationFee: string
    price: number | null
    duration: number
    format: string
    creator?: {
      id: string
      name: string | null
      username: string | null
      profilePictureUrl?: string | null
    } | null
  }) => {
    setSelectedSession({
      ...session,
      expert: {
        id: session.creator?.id || "",
        name: session.creator?.name || null,
        username: session.creator?.username || null,
        profilePictureUrl: session.creator?.profilePictureUrl,
      },
    })
    setBookingsInitialTab("completed")
    setBookingsModalOpen(true)
  }

  return (
    <div className={className}>
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">{t("coaching.loading")}</p>
        </div>
      )}

      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">{t("coaching.error")}</p>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="mt-0 md:px-5">
          {showHeader && (
            <>
              <div className="flex justify-between items-center gap-2">
                <h1 className="text-[18px] leading-[30px] font-monteserat font-semibold uppercase">
                  {t("coaching.title")}
                </h1>
                <div className="lg:hidden flex">
                  {showSearch ? (
                    <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                      <Search className="w-4 h-4 text-primary/50 shrink-0" />
                      <Input
                        type="text"
                        placeholder="Search..."
                        value={localSearch}
                        onChange={e => setLocalSearch(e.target.value)}
                        className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-6 w-6 text-primary/50"
                        onClick={() => {
                          setShowSearch(false)
                          setLocalSearch("")
                        }}
                        aria-label="Close search"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="opacity-50 cursor-pointer h-5"
                      onClick={() => (isGuest ? setShowLoginModal(true) : setShowSearch(true))}
                      aria-label="Search"
                    >
                      <Search className="w-5 h-5 text-primary" />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-3 text-[14px] font-regular leading-[25px]">
                {t("coaching.description")}
              </p>
            </>
          )}

          {sessions.length === 0 ? (
            <div className="h-[250px] mt-4">
              <p className="text-stone mt-3 text-[14px] font-regular leading-[25px]">
                {t("coaching.notFound")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:my-10 w-full">
              {sessions.map(session => (
                <CoachingSessionCard
                  key={session.id}
                  id={session.id}
                  slug={session.slug}
                  title={session.title}
                  coverImage={session.coverImage}
                  format={session.format}
                  duration={session.duration}
                  participationFee={session.participationFee}
                  price={session.price}
                  description={session.description}
                  tags={session.tags}
                  creator={session.creator}
                  isOwner={session.isOwner}
                  isBookmarked={session.isSaved}
                  location={session.streetAddress}
                  stats={session.stats}
                  isPaused={isCoachingPaused}
                  onBookmarkClick={() => toggleSave.mutate(session.id)}
                  onEditClick={
                    session.isOwner
                      ? () => navigate(`/coaching-sessions/edit/${session.id}`)
                      : undefined
                  }
                  onDeleteClick={
                    session.isOwner ? () => handleDeleteClick(session.id, session.title) : undefined
                  }
                  onBookingsClick={
                    session.isOwner
                      ? () =>
                          handleBookingsClick({
                            id: session.id,
                            title: session.title,
                            coverImage: session.coverImage,
                            participationFee: session.participationFee,
                            price: session.price,
                            duration: session.duration,
                            format: session.format,
                            creator: session.creator,
                          })
                      : undefined
                  }
                  onCompletedClick={
                    session.isOwner
                      ? () =>
                          handleCompletedClick({
                            id: session.id,
                            title: session.title,
                            coverImage: session.coverImage,
                            participationFee: session.participationFee,
                            price: session.price,
                            duration: session.duration,
                            format: session.format,
                            creator: session.creator,
                          })
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog - outside conditional blocks */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={open => {
          setDeleteDialogOpen(open)
          // Clear sessionToDelete when dialog closes via ESC/outside click
          if (!open) {
            setSessionToDelete(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Coaching Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{sessionToDelete?.title}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteSession.isPending}
            >
              {deleteSession.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Bookings Modal */}
      {selectedSession && (
        <SessionBookingsModal
          open={bookingsModalOpen}
          onOpenChange={setBookingsModalOpen}
          sessionId={selectedSession.id}
          sessionTitle={selectedSession.title}
          coverImage={selectedSession.coverImage}
          participationFee={selectedSession.participationFee}
          price={selectedSession.price}
          duration={selectedSession.duration}
          format={selectedSession.format}
          initialTab={bookingsInitialTab}
          expert={selectedSession.expert}
        />
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
